import BaseAccordion from 'components/Accordions/BaseAccordion';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { usePerms } from 'contexts/PermsContext';
import { getRelatedGlobalActionsFromDevice } from 'features/GlobalActions/features/ActionList/utils/actionListUtils';
import { selectDeviceTypeMap } from 'features/Integrations/state/selectors/integrationSelectors';
import { systemSearchFilter } from 'features/Integrations/utils/integrationSearchUtils';
import { tryParseString } from 'features/Integrations/utils/parser.utils';
import { useSystemStructure } from 'features/System/systemStructure';
import useWindow from 'hooks/useWindow';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  useAddConnectedDeviceMutation,
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
  useRemoveConnectedDeviceMutation,
  useSetDeviceSirenLinkMutation,
  useUpdateConnectedDeviceMutation,
} from 'services/devices/devices.api';
import { useGetGlobalActionsQuery } from 'services/globalActions/globalActions.api';
import {
  useFusionAddIntegrationMutation,
  useFusionDeleteIntegrationMutation,
  useFusionGetIntegrationsQuery,
  useFusionUpdateIntegrationMutation,
} from 'services/integrations/integrations.api';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import {
  getDeviceIndexMap,
  getLicenseSystems,
  getSystemIndexMap,
} from '../../utils/license.utils';
import AddEditGroupModal from '../ConnectedDeviceGroups/features/AddEditGroupModal';
import Accordion from './components/Accordion';
import BulkAddProgressModal from './components/BulkAddProgressModal';
import DeviceModal from './components/DeviceModal/DeviceModal';
import {
  getDeviceIdAlreadyInUseMessage,
  hasDuplicateDeviceId,
  resolveDeviceMutationMessage,
  resolveSubmittedDeviceId,
} from './components/DeviceModal/utils/deviceModalUtils';
import SystemModal from './components/SystemModal';
import Table from './components/Table';
import { bulkAddDevices } from './utils/bulkAddDevicesUtil';

const ConnectedSystemsWeb = ({}) => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [addConnectedDevice, { isLoading: isAddingDevice }] = useAddConnectedDeviceMutation();
  const [updateConnectedDevice, { isLoading: isUpdatingDevice }] =
    useUpdateConnectedDeviceMutation();
  const [removeConnectedDevice] = useRemoveConnectedDeviceMutation();
  const [setDeviceSirenLink] = useSetDeviceSirenLinkMutation();
  const [fusionAddIntegration] = useFusionAddIntegrationMutation();
  const [fusionUpdateIntegration] = useFusionUpdateIntegrationMutation();
  const [fusionDeleteIntegration] = useFusionDeleteIntegrationMutation();
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const systemStructure = useSystemStructure();
  const perms = usePerms();
  const canViewUSGS = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['ViewUSGSMonitoringStationConnections'],
  );
  const { width } = useWindow();
  const [newEntity, setNewEntity] = useState(); // Used to determine if editing a system or device
  const [toEdit, setToEdit] = useState(); // Holds the System or Device being edited
  const [toDelete, setToDelete] = useState(false); // Holds the System or Device being deleted
  const [loading, setLoading] = useState(false);
  const [systemRowIdEllipsisOpen, setSystemRowIdEllipsisOpen] = useState({});
  const [deviceRowIdEllipsisOpen, setDeviceRowIdEllipsisOpen] = useState({});
  const [bulkAddState, setBulkAddState] = useState(null); // null = idle, object = { progress }
  const [deviceModalMessage, setDeviceModalMessage] = useState(null);
  const deviceIndexMap = getDeviceIndexMap(connectedDevices);
  // Built from the raw lists, not from connectedSystemsHierarchy, so the license index a
  // system is given does not change when the hierarchy is narrowed by search or permissions.
  const systemIndexMap = useMemo(
    () => getSystemIndexMap(getLicenseSystems(integrations, connectedDevices)),
    [integrations, connectedDevices],
  );
  const oldPasswordRef = useRef(null);

  const openEditFormForSystemOrDevice = useCallback((typeOfEntity, entityBeingEdited) => {
    setDeviceModalMessage(null);

    setNewEntity(typeOfEntity); // "system" or "device" (string)
    setToEdit(entityBeingEdited); // system or device object
  }, []);

  const prepareSystemOrDeviceForDeletion = useCallback((entity) => {
    setToDelete({
      ...entity,
      name: entity?.system_name || entity?.alias || 'N/A',
      type: entity?.system_name ? 'system' : 'device',
    });
  }, []);

  const handleAddButton = (entityBeingAdded) => {
    setDeviceModalMessage(null);
    setNewEntity(entityBeingAdded);
    setToEdit(null);
  };

  const handleAddAndEditSystem = async (newSystem) => {
    setLoading(true);

    try {
      const systemPayload = { ...newSystem };
      if (oldPasswordRef.current) {
        systemPayload.oldPassword = oldPasswordRef.current;
      }

      let res;

      if (!toEdit) {
        res = await fusionAddIntegration(systemPayload).unwrap();
        notifySuccessToast('System', { isAddOperation: true });
      } else {
        res = await fusionUpdateIntegration(systemPayload).unwrap();
        notifySuccessToast('System', { isAddOperation: false });
      }

      if (res.status === 200) {
        oldPasswordRef.current = null;
        setToEdit(null);
        setNewEntity(false);
      }
    } catch (err) {
      console.error('handleAddAndEditSystem failed');
      notifyErrorToast('system', { isAddOperation: !toEdit });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAndEditDevice = async (newDevice) => {
    const { device, ...newDeviceVar } = newDevice;
    const formattedNewDevice = { ...device, ...newDeviceVar };
    const submittedDeviceId = resolveSubmittedDeviceId(formattedNewDevice);

    if (hasDuplicateDeviceId(formattedNewDevice, connectedDevices)) {
      setDeviceModalMessage({
        type: 'error',
        message: getDeviceIdAlreadyInUseMessage(submittedDeviceId),
      });
      return;
    }

    setDeviceModalMessage(null);

    try {
      if (!toEdit) {
        if (!formattedNewDevice.coordinates || typeof formattedNewDevice.coordinates === 'string') {
          formattedNewDevice.coordinates = { lat: 0.0, lng: 0.0 };
        }

        if (!formattedNewDevice.location || typeof formattedNewDevice.location === 'string') {
          formattedNewDevice.location = { lat: 0.0, lng: 0.0 };
        }

        const added = await addConnectedDevice({ ...formattedNewDevice }).unwrap();
        notifySuccessToast('Device', { isAddOperation: true });

        // A camera or a siren can be paired on the way in. The link is one row
        // addressed by the camera: a new camera pairs with the chosen siren
        // (`siren_uid`), a new siren pairs with the chosen camera
        // (`camera_uid`). Either way the new device's id only exists once the
        // add has returned, so the pairing is applied here, not in the modal.
        //
        // Deliberately not fatal: the device IS added at this point, and
        // failing the whole operation over an optional pairing would be worse
        // than telling the operator to set it from the device's edit form.
        const newId = added?.uid ?? added?.result?.uid;
        const pairing = !newId
          ? null
          : formattedNewDevice?.siren_uid
            ? { cameraId: newId, sirenUid: Number(formattedNewDevice.siren_uid) }
            : formattedNewDevice?.camera_uid
              ? { cameraId: Number(formattedNewDevice.camera_uid), sirenUid: newId }
              : null;
        if (pairing) {
          try {
            await setDeviceSirenLink(pairing).unwrap();
          } catch (linkErr) {
            console.error('Device added, but the camera/siren pairing failed:', linkErr);
            setDeviceModalMessage({
              type: 'error',
              message:
                'The device was added, but it could not be paired. ' +
                'Set the pairing from the device’s edit form (Advanced).',
            });
          }
        }
      } else {
        const updatedDeviceInfo = {
          id: formattedNewDevice?.id,
          node_id: formattedNewDevice?.node_id,
          new_node_id: Number(formattedNewDevice?.new_node_id),
          name: formattedNewDevice?.name,
          alias: formattedNewDevice?.alias,
          location: formattedNewDevice?.location,
          coordinates: tryParseString(formattedNewDevice?.coordinates),
          device_type: formattedNewDevice?.device_type,
          groups: formattedNewDevice?.groups || [],
        };

        await updateConnectedDevice(updatedDeviceInfo).unwrap();
        notifySuccessToast('Device', { isAddOperation: false });
      }

      setToEdit(null);
      setNewEntity(null);
      setDeviceModalMessage(null);
    } catch (err) {
      console.error('handleAddAndEditDevice failed');
      const validationMessage = resolveDeviceMutationMessage(err, submittedDeviceId);
      if (validationMessage) {
        setDeviceModalMessage({
          type: 'error',
          message: validationMessage,
        });
        return;
      }

      notifyErrorToast('device', { isAddOperation: !toEdit }, { errorPayload: err });
    }
  };

  const handleBulkAddDevices = async (devicePayloads) => {
    // Close the device modal immediately and show the progress modal.
    setNewEntity(false);
    setBulkAddState({
      progress: {
        total: devicePayloads.length,
        current: 0,
        succeeded: 0,
        failed: 0,
        errors: [],
        isComplete: false,
      },
    });

    const addFn = async (payload) => {
      const { device, ...rest } = payload;
      const formatted = { ...device, ...rest };
      if (!formatted.coordinates || typeof formatted.coordinates === 'string') {
        formatted.coordinates = { lat: 0.0, lng: 0.0 };
      }
      if (!formatted.location || typeof formatted.location === 'string') {
        formatted.location = { lat: 0.0, lng: 0.0 };
      }
      await addConnectedDevice(formatted).unwrap();
    };

    await bulkAddDevices(devicePayloads, addFn, (progress) => {
      setBulkAddState({ progress });
    });
  };

  const handleCancelModal = () => {
    setDeviceModalMessage(null);
    {
    setNewEntity(false);
  };
    if (oldPasswordRef) oldPasswordRef.current = null;
  };

  const handleDeleteDeviceAndSystem = async (confirmDeletion) => {
    const entityType = toDelete?.type;

    try {
      if (confirmDeletion) {
        switch (entityType) {
          case 'system':
            await fusionDeleteIntegration(toDelete).unwrap();
            notifySuccessToast('System', { isAddOperation: false, isDeleteOperation: true });
            break;
          case 'device':
            await removeConnectedDevice(toDelete).unwrap();
            notifySuccessToast('Device', { isAddOperation: false, isDeleteOperation: true });
            break;
          default:
            break;
        }
      }

      setToDelete(null);
    } catch (err) {
      console.error('Error in handleDeleteDeviceOrSystem:', err);

      if (entityType === 'system') {
        notifyErrorToast('system', { isAddOperation: false, isDeleteOperation: true });
      } else if (entityType === 'device') {
        notifyErrorToast('device', { isAddOperation: false, isDeleteOperation: true });
      }
    }
  };

  const connectedSystemsHierarchy = useMemo(() => {
    return systemStructure.buildConnectedSystemsHierarchy({
      integrations,
      connectedDevices,
      deviceTypes,
      perms,
      canViewUSGS,
    });
  }, [integrations, systemStructure, perms, deviceTypes, connectedDevices, canViewUSGS]);

  const { data: globalActionList = [] } = useGetGlobalActionsQuery();

  return (
    <div className="d-flex flex-column gap-3 flex-grow-1 min-h-0 overflow-hidden">
      <div className="d-flex flex-column flex-grow-1 min-h-0 pe-1 overflow-hidden">
        {width >= 992 && (
          <div className="d-flex flex-column h-100 min-h-0 overflow-hidden">
            <Table
              deviceIndexMap={deviceIndexMap}
              systemIndexMap={systemIndexMap}
              connectedSystemsHierarchy={connectedSystemsHierarchy}
              openEditFormForSystemOrDevice={openEditFormForSystemOrDevice}
              prepareSystemOrDeviceForDeletion={prepareSystemOrDeviceForDeletion}
              deviceRowIdEllipsisOpen={deviceRowIdEllipsisOpen}
              setDeviceRowIdEllipsisOpen={setDeviceRowIdEllipsisOpen}
              systemRowIdEllipsisOpen={systemRowIdEllipsisOpen}
              setSystemRowIdEllipsisOpen={setSystemRowIdEllipsisOpen}
            />
          </div>
        )}
        {width < 992 && (
          <div>
            <Accordion
              deviceIndexMap={deviceIndexMap}
              systemIndexMap={systemIndexMap}
              connectedSystemsHierarchy={connectedSystemsHierarchy}
              openEditFormForSystemOrDevice={openEditFormForSystemOrDevice}
              prepareSystemOrDeviceForDeletion={prepareSystemOrDeviceForDeletion}
              deviceRowIdEllipsisOpen={deviceRowIdEllipsisOpen}
              setDeviceRowIdEllipsisOpen={setDeviceRowIdEllipsisOpen}
              systemRowIdEllipsisOpen={systemRowIdEllipsisOpen}
              setSystemRowIdEllipsisOpen={setSystemRowIdEllipsisOpen}
            />
          </div>
        )}
      </div>
      {/* Controls */}
      <div className="d-flex flex-column flex-md-row gap-3 flex-shrink-0">
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditIntegrations']) && (
          <>
            <Button
              className="w-100 w-md-3xs py-3 py-md-4"
              onClick={() => handleAddButton('system')}
              variant="primary"
            >
              New System
            </Button>
            <Button
              className="w-100 w-md-3xs py-3 py-md-4"
              onClick={() => handleAddButton('device')}
              variant="primary"
            >
              New Device
            </Button>
            <Button
              className="w-100 w-md-3xs py-3 py-md-4"
              onClick={() => handleAddButton('deviceGroup')}
              variant="primary"
            >
              New Device Group
            </Button>
          </>
        )}
      </div>
      {/* New System Modal */}
      {newEntity === 'system' && (
        <SystemModal
          onSubmit={handleAddAndEditSystem}
          onCancel={handleCancelModal}
          onOldPasswordVerified={(pwd) => {
            oldPasswordRef.current = pwd;
          }}
          system={toEdit}
          loading={loading}
        />
      )}
      {/* New Device Modal */}
      {newEntity === 'device' && (
        <DeviceModal
          onSubmit={handleAddAndEditDevice}
          onBulkSubmit={handleBulkAddDevices}
          onCancel={handleCancelModal}
          device={toEdit}
          message={deviceModalMessage}
          loading={isAddingDevice || isUpdatingDevice}
        />
      )}
      {newEntity === 'deviceGroup' && (
        <AddEditGroupModal modalOpen={-1} setModalOpen={handleCancelModal} />
      )}
      {/* Bulk Add Progress Modal */}
      {bulkAddState && (
        <BulkAddProgressModal
          show
          progress={bulkAddState.progress}
          onClose={() => setBulkAddState(null)}
        />
      )}
      {/* Delete modal */}
      <ConfirmationModal
        show={toDelete}
        handleShow={() => handleDeleteDeviceAndSystem(false)}
        bodyTxt={(() => {
          const relatedGlobalActions = getRelatedGlobalActionsFromDevice(
            toDelete?.id,
            globalActionList,
          );
          return (
            <div>
              <p>
                Are you sure you want to delete <strong>{toDelete?.name}</strong> from the
                workplace?
              </p>
              {relatedGlobalActions.length > 0 && (
                <div>
                  <p>
                    <br />
                    This will affect the following Global Actions:
                  </p>

                  <BaseAccordion
                    header={<span>Related Global Actions ({relatedGlobalActions.length})</span>}
                    rounded
                    bodyBackground="#436C9A"
                    style={{ borderRadius: '12px', overflow: 'hidden' }}
                  >
                    <div
                      className="customScrollbar-Y m-0 ps-3 overflow-y-auto"
                      style={{ maxHeight: 200 }}
                    >
                      {relatedGlobalActions.map((title, idx) => (
                        <div className="border-bottom border-1 w-100" key={title + idx}>
                          {title}
                        </div>
                      ))}
                    </div>
                  </BaseAccordion>
                </div>
              )}
            </div>
          );
        })()}
        cancelBtnTxt="Cancel"
        confirmBtnTxt="Yes"
        confirmButtonStyle={{
          backgroundColor: 'red',
          color: 'white',
        }}
        cancelHandler={() => handleDeleteDeviceAndSystem(false)}
        confirmHandler={() => handleDeleteDeviceAndSystem(true)}
      />
    </div>
  );
};

export default ConnectedSystemsWeb;
